/**
 * Subscriber-side event personalization: the preference vocabulary, the
 * system defaults, resolution order (per-calendar ?? user default ?? system)
 * and the title formatter shared by the in-app preview and the ICS renderer.
 */
import { hasSportsTag } from "./sportsTags.js";
export const EVENT_PREFERENCE_KEYS = [
    "titleStyle",
    "myTeamFirst",
    "separator",
    "emoji",
    "whereToWatch",
    "spoilerSafe",
    "reminder",
];
/** Keys the feed render applies; any other key renders its system value and reports `not_yet_available`. */
export const RELEASED_EVENT_PREFERENCE_KEYS = Object.freeze([
    "titleStyle",
    "myTeamFirst",
    "separator",
    "emoji",
    "reminder",
]);
/** `reason` codes a capability reports when a key is unsupported. */
export const CAPABILITY_REASONS = Object.freeze({
    /** The key isn't released yet. */
    notYetAvailable: "not_yet_available",
    /** The calendar's feed is never personalized (an external link or the elections feed). */
    notPersonalizable: "not_personalizable",
    /** The calendar's events carry no matchup to rebuild titles from. */
    noMatchups: "no_matchups",
    /** The calendar isn't one team's, so no side of a matchup is "my team". */
    notTeamCalendar: "not_team_calendar",
    /** The calendar's matchups carry no short team names, so the short title style would change nothing. */
    noShortNames: "no_short_names",
    /** The calendar's events carry no channel or streaming service to show. */
    noBroadcastData: "no_broadcast_data",
    /** The calendar's past events carry neither a result nor an episode synopsis to hide. */
    noResultsOrSynopses: "no_results_or_synopses",
});
/** `Calendar.link` values whose feed Stanza renders; any other link is served from its origin, unpersonalized. */
export const PERSONALIZABLE_CALENDAR_LINKS = Object.freeze(["scraped", "scraped-legacy-manual"]);
export const TITLE_STYLES = ["full", "short"];
/** `original` keeps the separator the event's title was built with. */
export const TITLE_SEPARATORS = ["original", "vs", "at", "dash"];
export const REMINDER_OPTIONS = ["none", "30m", "2h", "1d"];
/** Text each chosen separator renders as inside a matchup title. */
export const SEPARATOR_TEXT = Object.freeze({
    vs: "vs",
    at: "@",
    dash: "–",
});
/** Minutes before the event start for each reminder option; `null` means no VALARM. */
export const REMINDER_MINUTES = Object.freeze({
    none: null,
    "30m": 30,
    "2h": 120,
    "1d": 1440,
});
/** Rendering with no preferences set; timed events alert 30 minutes before, all-day ones at 19:00 the day before. */
export const SYSTEM_EVENT_PREFERENCES = Object.freeze({
    titleStyle: "full",
    myTeamFirst: false,
    separator: "original",
    emoji: null,
    whereToWatch: true,
    spoilerSafe: false,
    reminder: "30m",
});
/** Stored `emoji` value meaning "no emoji here", so a calendar can opt out of a default emoji (`null` inherits). */
export const NO_EMOJI = "";
/** Preferences a matchup's stored title renders with, independent of the subscriber defaults. */
const STORED_TITLE_PREFERENCES = Object.freeze({
    titleStyle: "full",
    myTeamFirst: false,
    separator: "original",
    emoji: null,
    whereToWatch: true,
    spoilerSafe: false,
    reminder: "none",
});
const EMOJI_SEQUENCE = /(?:[0-9#*]\uFE0F?\u20E3|\p{Regional_Indicator}{2}|(?:(?=\p{Extended_Pictographic})\p{Emoji_Presentation}(?:\uFE0F|\p{Emoji_Modifier})?|\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}))(?:[\u{E0020}-\u{E007E}]+\u{E007F})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})?)*)/u;
/**
 * Resolve the effective preferences for one calendar.
 * @param overrides - Per-calendar choices; `null` / missing keys inherit (`NO_EMOJI` is a choice, not a gap).
 * @param defaults - The user's defaults; `null` / missing keys fall back to the system value.
 * @param capabilities - Keys the calendar supports; an unsupported key always renders the system value.
 * @returns A complete preference set.
 */
export function resolveEventPreferences(overrides, defaults, capabilities) {
    const resolved = { ...SYSTEM_EVENT_PREFERENCES };
    for (const key of EVENT_PREFERENCE_KEYS) {
        if (capabilities && capabilities[key]?.supported !== true)
            continue;
        resolved[key] = overrides?.[key] ?? defaults?.[key] ?? SYSTEM_EVENT_PREFERENCES[key];
    }
    return resolved;
}
/**
 * Build an event title from its parts under the given preferences.
 * With system preferences a matchup renders exactly as its stored title.
 * @param parts - A `matchup` with the subscriber's `mySide`, or a plain `title`.
 * @param prefs - Effective preferences for the calendar.
 * @returns The title the subscriber sees.
 */
export function formatPersonalizedTitle(parts, prefs) {
    const emoji = prefs.emoji ? `${prefs.emoji} ` : "";
    const { matchup } = parts;
    if (!isRenderableMatchup(matchup))
        return `${emoji}${parts.title ?? ""}`;
    const { first, second, separator } = arrangeMatchup(matchup, parts.mySide, prefs);
    const label = (side) => `${teamLabel(matchup[side], prefs)}${prefs.spoilerSafe ? "" : resultTag(matchup, side)}`;
    const prefix = matchup.prefix ? `${matchup.prefix} ` : "";
    return `${emoji}${prefix}${label(first)} ${separator} ${label(second)}`;
}
/**
 * The title stored on an event: its status prefix, then the matchup in its own order with W/L/D tags.
 * @param matchup - An event's matchup.
 * @returns e.g. "❗️Postponed: Phoenix Suns at Golden State Warriors".
 * @throws When the matchup lacks a side name or its separator.
 */
export function formatMatchupTitle(matchup) {
    if (!isRenderableMatchup(matchup))
        throw new Error("A matchup title needs both side names and a separator");
    return formatPersonalizedTitle({ matchup }, STORED_TITLE_PREFERENCES);
}
/**
 * Score line for a played matchup, as feeds show it in event descriptions: home first, full names.
 * Title preferences don't change it; spoiler-safe hides it.
 * @param parts - A `matchup`.
 * @param prefs - Effective preferences for the calendar.
 * @returns e.g. "Golden State Warriors(121) - Phoenix Suns(112)", or `null` without both scores or under spoiler-safe.
 */
export function formatMatchupScore(parts, prefs) {
    const { matchup } = parts;
    if (prefs.spoilerSafe || !isRenderableMatchup(matchup))
        return null;
    const { home, away } = matchup;
    if (typeof home.score !== "number" || typeof away.score !== "number")
        return null;
    return `${home.name}(${home.score}) - ${away.name}(${away.score})`;
}
/**
 * Pull the first complete emoji (including ZWJ, skin-tone and flag sequences) out of free text.
 * @param value - Raw input, e.g. what the OS emoji keyboard typed.
 * @returns The emoji, or `null` when the text has none.
 */
export function extractFirstEmoji(value) {
    if (!value)
        return null;
    return value.match(EMOJI_SEQUENCE)?.[0] ?? null;
}
/**
 * @param value - Candidate preference value.
 * @returns True when the value is exactly one emoji and nothing else.
 */
export function isSingleEmoji(value) {
    return Boolean(value) && extractFirstEmoji(value) === value;
}
/**
 * @param value - A stored or submitted `emoji` preference.
 * @returns True for `null` (inherit), `NO_EMOJI`, or exactly one emoji.
 */
export function isValidEmojiPreference(value) {
    return value === null || value === NO_EMOJI || (typeof value === "string" && isSingleEmoji(value));
}
/**
 * @param calendar - A calendar with its `tags` and `tvStatus`.
 * @returns The calendar's kind for personalization previews: sports, TV, or anything else.
 */
export function calendarKind(calendar) {
    if (hasSportsTag(calendar?.tags))
        return "sports";
    if (calendar?.tvStatus)
        return "tv";
    return "manual";
}
/**
 * @param link - A calendar's `link`.
 * @returns Whether Stanza renders that calendar's feed, so preferences can apply to it.
 */
export function isPersonalizableCalendarLink(link) {
    return typeof link === "string" && PERSONALIZABLE_CALENDAR_LINKS.includes(link);
}
/**
 * Mark every key outside `releasedKeys` unsupported (`not_yet_available`).
 * @param capabilities - Data-derived capabilities; released keys missing here count as supported.
 * @param releasedKeys - Defaults to `RELEASED_EVENT_PREFERENCE_KEYS`.
 * @returns A capability for every preference key.
 */
export function withReleasedKeys(capabilities = null, releasedKeys = RELEASED_EVENT_PREFERENCE_KEYS) {
    return Object.fromEntries(EVENT_PREFERENCE_KEYS.map((key) => [
        key,
        releasedKeys.includes(key)
            ? capabilities?.[key] ?? { supported: true }
            : { supported: false, reason: CAPABILITY_REASONS.notYetAvailable },
    ]));
}
/**
 * Order and separator of a matchup title: a chosen @ reads away-at-home so it puts away first,
 * the event's own separator keeps the event's order, and my team first then leads with `mySide`.
 * @param matchup - An event's matchup.
 * @param mySide - The subscribed calendar's side, if any.
 * @param prefs - Effective preferences for the calendar.
 * @returns The leading and trailing sides and the separator text between them.
 */
function arrangeMatchup(matchup, mySide, prefs) {
    const chosen = prefs.separator !== "original" && Object.prototype.hasOwnProperty.call(SEPARATOR_TEXT, prefs.separator)
        ? prefs.separator
        : null;
    const side = mySide === "home" || mySide === "away" ? mySide : null;
    let first = chosen === "at" || matchup.order !== "home-first" ? "away" : "home";
    const leadsAsMyTeam = Boolean(prefs.myTeamFirst && side && side !== first);
    if (leadsAsMyTeam)
        first = side;
    // An at-separator reads away-at-home, so a home team moved to the front takes vs instead.
    const homeMovedFirst = leadsAsMyTeam && first === "home";
    const separator = chosen
        ? SEPARATOR_TEXT[chosen === "at" && homeMovedFirst ? "vs" : chosen]
        : matchup.separator === "at" && homeMovedFirst
            ? "vs."
            : matchup.separator;
    return { first, second: first === "home" ? "away" : "home", separator };
}
/**
 * @param matchup - An event's matchup, possibly partial when it comes from the network.
 * @returns Whether it has both side names and a separator, so a title can be built from it.
 */
function isRenderableMatchup(matchup) {
    const named = (team) => typeof team?.name === "string" && team.name.length > 0;
    return Boolean(matchup && named(matchup.home) && named(matchup.away) && typeof matchup.separator === "string" && matchup.separator);
}
/**
 * @param team - One side of a matchup.
 * @param prefs - Effective preferences for the calendar.
 * @returns The short name under the short title style when the team has one, else the full name.
 */
function teamLabel(team, prefs) {
    return prefs.titleStyle === "short" && team.shortName ? team.shortName : team.name;
}
/**
 * @param matchup - An event's matchup.
 * @param side - The side to tag.
 * @returns " (W)", " (L)" or " (D)" for a decided game, otherwise "".
 */
function resultTag(matchup, side) {
    if (!matchup.winner)
        return "";
    if (matchup.winner === "draw")
        return " (D)";
    return matchup.winner === side ? " (W)" : " (L)";
}
//# sourceMappingURL=eventPersonalization.js.map